import React, { CSSProperties, ReactNode, useState, useEffect, useCallback } from 'react'
import styles from './SlideInAnimation.module.css'

interface SlideInAnimationProps {
  children: ReactNode
  isVisible: boolean
  direction?: 'up' | 'down' | 'left' | 'right'
  offset?: number // offset in pixels for the animation distance (default: 56)
  duration?: string
  exitDuration?: string
  easing?: string
  style?: CSSProperties
  className?: string
  id?: string
  showWithAnimation?: boolean
  hideWithAnimation?: boolean
}

const SlideInAnimation: React.FC<SlideInAnimationProps> = ({
  children,
  isVisible,
  direction = 'up',
  exitDuration,
  style,
  className,
  id,
  showWithAnimation: addWithAnimation = true,
  hideWithAnimation: closeWithAnimation = true,
}) => {
  const [shouldRender, setShouldRender] = useState(isVisible)
  const [animationClass, setAnimationClass] = useState<string>('')
  const [animationDuration, setAnimationDuration] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true)
    }

    // Handle animations when isVisible changes
    if (isVisible) {
      const enterClass = addWithAnimation
        ? styles[`enter${direction.charAt(0).toUpperCase() + direction.slice(1)}`]
        : styles.enterWithoutAnimation
      setAnimationClass(enterClass)
      setAnimationDuration(undefined)
    } else {
      const exitClass = closeWithAnimation
        ? styles[`exit${direction.charAt(0).toUpperCase() + direction.slice(1)}`]
        : styles.exitWithoutAnimation
      setAnimationClass(exitClass)
      setAnimationDuration(exitDuration)
    }
  }, [isVisible, direction, addWithAnimation, closeWithAnimation, exitDuration])
  
  const handleAnimationEnd = useCallback((e: React.AnimationEvent) => {
    // Ignore animationend events bubbling up from child elements
    if (e.target !== e.currentTarget) return
    if (!isVisible) {
      setShouldRender(false)
    } else {
      // Once the enter animation settles, drop the transform-bearing class so the
      // element is no longer GPU-composited at a sub-pixel offset, which renders
      // text/icons blurry in Safari.
      setAnimationClass(styles.enterWithoutAnimation)
    }
  }, [isVisible])
  
  if (!closeWithAnimation && !isVisible) return null
  if (!shouldRender) return null

  return (
    <div
      id={id}
      className={`${animationClass} ${className || ''}`}
      onAnimationEnd={handleAnimationEnd}
      style={{ ...style, ...(animationDuration ? { animationDuration } : {}) }}
    >
      {children}
    </div>
  )
}

export default SlideInAnimation